import { getRoutes } from "../utilities/routes";

// Types
export interface ProcessSection {
  id: number;
  sectionNumber: number;
  name: string;
  description?: string;
}

export interface CreateProcessSectionRequest {
  sectionNumber: number;
  name: string;
  description?: string;
}

export interface UpdateProcessSectionRequest {
  sectionNumber: number;
  name: string;
  description?: string;
}

export interface ApiResponse<T> {
  status: boolean;
  data: T;
  message?: string;
}

// API functions
export const fetchProcessSections = async (
  token: string
): Promise<ProcessSection[]> => {
  const routes = getRoutes();
  const response = await fetch(routes.ProcessSectionsRoutes, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to fetch process sections: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const fetchProcessSectionById = async (
  id: number,
  token: string
): Promise<ProcessSection> => {
  const routes = getRoutes();
  const response = await fetch(`${routes.ProcessSectionsRoutes}${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to fetch process section: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const createProcessSection = async (
  data: CreateProcessSectionRequest,
  token: string
): Promise<ApiResponse<ProcessSection>> => {
  const routes = getRoutes();
  const response = await fetch(routes.ProcessSectionsRoutes, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to create process section: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const updateProcessSection = async (
  id: number,
  data: UpdateProcessSectionRequest,
  token: string
): Promise<ApiResponse<ProcessSection>> => {
  const routes = getRoutes();
  const response = await fetch(`${routes.ProcessSectionsRoutes}${id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to update process section: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};

export const deleteProcessSection = async (
  id: number,
  token: string
): Promise<ApiResponse<string>> => {
  const routes = getRoutes();
  const response = await fetch(`${routes.ProcessSectionsRoutes}${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to delete process section: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  return response.json();
};
